import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Plus, Trash2 } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';

const iconOptions = [
 { value: 'FileText', label: 'File Text' },
 { value: 'Shield', label: 'Shield' },
 { value: 'Users', label: 'Users' },
 { value: 'AlertCircle', label: 'Alert Circle' },
 { value: 'Scale', label: 'Scale' },
 { value: 'CheckCircle', label: 'Check Circle' },
];

const CreateTermsOfServiceModal = ({
 isOpen,
 onClose,
 onSave,
 section = null,
 isEdit = false,
 isLoading = false
}) => {
 const [formData, setFormData] = useState({
 title: '',
 content: [],
 icon: 'FileText',
 sort_order: 0,
 is_active: true,
 });
 const [errors, setErrors] = useState({});
 const [newParagraph, setNewParagraph] = useState('');

 useEffect(() => {
 if (isEdit && section) {
 setFormData({
 title: section.title || '',
 content: section.content || [],
 icon: section.icon || 'FileText',
 sort_order: section.sort_order || 0,
 is_active: section.is_active ?? true,
 });
 } else {
 setFormData({
 title: '',
 content: [],
 icon: 'FileText',
 sort_order: 0,
 is_active: true,
 });
 }
 setErrors({});
 setNewParagraph('');
 }, [isEdit, section, isOpen]);

 const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
 setFormData(prev => ({
 ...prev,
 [name]: type === 'checkbox' ? checked : value
 }));

 if (errors[name]) {
 setErrors(prev => ({
 ...prev,
 [name]: ''
 }));
 }
 };

 const handleIconChange = (value) => {
 setFormData(prev => ({ ...prev, icon: value }));
 if (errors.icon) {
 setErrors(prev => ({ ...prev, icon: '' }));
 }
 };

 const handleAddParagraph = () => {
 if (newParagraph.trim()) {
 setFormData(prev => ({
 ...prev,
 content: [...prev.content, newParagraph.trim()]
 }));
 setNewParagraph('');
 }
 };

 const handleRemoveParagraph = (index) => {
 setFormData(prev => ({
 ...prev,
 content: prev.content.filter((_, i) => i !== index)
 }));
 };

 const validateForm = () => {
 const newErrors = {};
 if (!formData.title.trim()) newErrors.title = 'Title is required';
 if (!formData.content || formData.content.length === 0) {
 newErrors.content = 'At least one content paragraph is required';
 }
 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validateForm()) {
 return;
 }

 const submitData = {
 ...formData,
 sort_order: parseInt(formData.sort_order) || 0,
 };

 onSave(submitData);
 };

 const handleClose = () => {
 setErrors({});
 onClose();
 };

 if (!isOpen) return null;

 return createPortal(
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
 <div className="bg-surface rounded-lg shadow-sm max-w-4xl w-full max-h-[95vh] overflow-y-auto relative my-2 sm:my-4">
 {/* Modal Header */}
 <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-surface z-10">
 <div className="flex items-center">
 <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-ink to-neutral-900 flex items-center justify-center">
 <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
 </div>
 <h3 className="ml-2 sm:ml-3 text-base sm:text-lg font-medium text-gray-900 truncate">
 {isEdit ? 'Edit Terms Section' : 'Create Terms Section'}
 </h3>
 </div>
 <button
 onClick={handleClose}
 className="text-gray-400 hover:text-gray-600 p-1"
 >
 <X className="h-4 w-4 sm:h-5 sm:w-5" />
 </button>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6">
 <div className="space-y-4 sm:space-y-6">
 {/* Title */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Title <span className="text-red-500">*</span>
 </label>
 <Input
 name="title"
 value={formData.title}
 onChange={handleChange}
 placeholder="e.g., 1. Acceptance of Terms"
 error={errors.title}
 required
 />
 </div>

 {/* Icon and Sort Order */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Icon
 </label>
 <SearchSelect
 name="icon"
 value={formData.icon}
 onChange={handleIconChange}
 options={iconOptions}
 placeholder="Select an icon"
 error={errors.icon}
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Sort Order
 </label>
 <Input
 name="sort_order"
 type="number"
 value={formData.sort_order}
 onChange={handleChange}
 placeholder="0"
 min="0"
 error={errors.sort_order}
 />
 </div>
 </div>

 {/* Content Paragraphs */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">
 Content Paragraphs <span className="text-red-500">*</span>
 </label>
 <div className="space-y-2 mb-3">
 {formData.content.map((paragraph, index) => (
 <div key={index} className="flex items-start gap-2 bg-surface-muted p-3 rounded-lg">
 <div className="flex-1">
 <p className="text-sm text-gray-700">{paragraph}</p>
 </div>
 <button
 type="button"
 onClick={() => handleRemoveParagraph(index)}
 className="text-red-600 hover:text-red-800 p-1"
 >
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 ))}
 </div>
 <div className="flex gap-2">
 <TextArea
 value={newParagraph}
 onChange={(e) => setNewParagraph(e.target.value)}
 placeholder="Enter a new paragraph..."
 rows="3"
 className="flex-1"
 />
 <Button
 type="button"
 onClick={handleAddParagraph}
 variant="outline"
 className="self-start"
 >
 <Plus className="w-4 h-4" />
 </Button>
 </div>
 {errors.content && (
 <p className="mt-1 text-sm text-red-600">{errors.content}</p>
 )}
 </div>

 {/* Active Status */}
 <div className="flex items-center">
 <input
 id="is_active"
 name="is_active"
 type="checkbox"
 checked={formData.is_active}
 onChange={handleChange}
 className="h-4 w-4 text-ink border-gray-300 rounded focus:ring-ink"
 />
 <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
 Active
 </label>
 </div>
 </div>

 {/* Modal Footer */}
 <div className="mt-6 p-3 sm:p-4 md:p-6 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-surface z-10">
 <Button
 type="button"
 variant="outline"
 onClick={handleClose}
 disabled={isLoading}
 >
 Cancel
 </Button>
 <Button
 type="submit"
 variant="primary"
 loading={isLoading}
 className="w-full sm:w-auto"
 >
 {isEdit ? 'Update Section' : 'Create Section'}
 </Button>
 </div>
 </form>
 </div>
 </div>,
 document.body
 );
};

export default CreateTermsOfServiceModal;

